import React, { useCallback, useEffect, useState } from "react";
import { getInvoiceInfo, updateInvoice } from "@payping/redux/actions";
import {
  Button,
  Drawer,
  LegacyForm,
  Page,
  Progress,
  Space,
  Typography,
  message,
  useMessagedAsyncFn,
} from "@payping/web-components";
import type { UploadFile } from "@payping/web-components/components/upload/types";
import { getFileName, getFileType } from "@payping/utils/string";
import { dateFormats, toJalali, toUtc } from "@payping/utils/dateUtils";
import { useAsync, useAsyncFn } from "react-use";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DashboardRoutes } from "@/router/constant";
import { InoviceCreateStatus, invoiceV2Apis, productApis } from "@payping/api";
import type {
  InvoiceComplexDetailViewModel,
  InvoiceCreateRequestViewModel,
  InvoiceCreateScheduleViewModel,
  InvoiceEditRequestViewModel,
} from "@payping/api";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch } from "@/utils/redux";
import { CustomFormLayout } from "@/components/DashInvoice/CustomFormLayout";
import { Close } from "@payping/icons";
import FourthStepInvoice from "@/pages/Shop/Invoice/Create/fourthStep";
import ThirdStepInvoice from "@/pages/Shop/Invoice/Create/ThirdStep";
import SecondStepInvoice from "@/pages/Shop/Invoice/Create/secondStep";
import FirstStepInvoice from "@/pages/Shop/Invoice/Create/firstStep";
import DataCard from "@/pages/Shop/Invoice/Create/Anchor/dataCard";
import {
  CreateStepInvoiceEnum,
  Customer,
  CustomerConnection,
  InitialSettingType,
  InvoiceTypeEnum,
  Product,
  ProductConnection,
  SchedulingType,
  SubmitType,
} from "./type";

const InvoiceForm: React.FC = () => {
  // =========================================
  // Init
  // ========================================
  const { code } = useParams<{ code: string }>();
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [formStepOne] = LegacyForm.useForm<InitialSettingType>();
  const [formStepTwo] = LegacyForm.useForm<SchedulingType>();
  const [formStepThree] = LegacyForm.useForm<ProductConnection>();
  const [formStepFour] = LegacyForm.useForm<CustomerConnection>();
  const dispatch = useAppDispatch();
  const [selectedItems, setSelectedItems] = useState<Product[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer[]>([]);
  const [currents, setCurrents] = React.useState<CreateStepInvoiceEnum>(CreateStepInvoiceEnum.initialSetting);
  const [isSchedule, setIsSchedule] = useState<boolean>(false);
  const [manualPaymentNotesModalVisible, setManualPaymentNotesModalVisible] = useState(false);
  const [submitType, setSubmitType] = useState<SubmitType>("send");
  const [invoiceNotes, setInvoiceNotes] = useState<string>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showInvoiceType, setShowInvoiceType] = useState<InvoiceTypeEnum>(InvoiceTypeEnum.online);
  const [invoiceName, setInvoiceName] = useState<string>();
  const [invoiceNumber, setInvoiceNumber] = useState<string>();
  const [sentDate, setSentDate] = useState<string>();
  const [dueDate, setDueDate] = useState<string>();
  const [countProduct, setCountProduct] = useState<number>(0);
  const [sumTotalAmount, setSumTotalAmount] = useState<number>(0);
  const [coupon, setCoupon] = useState<number>();
  const [transport, setTransport] = useState<number>();
  const [totalAmount, setTotalAmount] = useState<number>();
  const [enteredText, setEnteredText] = useState<string>();
  const [showCoupon, setShowCoupon] = useState<boolean>();
  const [transportationCost, setTransportationCost] = useState<boolean>();
  const isCorrectionMode = location.pathname.includes("correction");
  const backFormat = dateFormats.standard.utcDateTime;

  // =========================================
  // Handlers
  // =========================================
  /**
   * remove extra cash units (e.g ریال, تومان) from message
   * there is only one unit (تومان) immediately after 'INVOICE_AMOUNT'
   */
  const nextStep = () => {
    setCurrents(currents + 1);
  };

  const prevStep = () => {
    setCurrents(currents - 1);
  };

  const calcItem = (item: Product) => {
    const calculatedItem = item as Product;

    if (!calculatedItem.amount) calculatedItem.amount = 0; // Tak item
    if (!calculatedItem.price) calculatedItem.price = 0; // Jame Khales
    if (!calculatedItem.totalAmount) calculatedItem.totalAmount = 0; // Kol
    if (!calculatedItem.discountAmount) calculatedItem.discountAmount = 0;
    if (!calculatedItem.discountType) calculatedItem.discountType = 0;
    if (!calculatedItem.quantity) calculatedItem.quantity = 1;
    calculatedItem.haveTax = !!(calculatedItem.haveTax || calculatedItem.tax);

    calculatedItem.price = (calculatedItem.quantity ?? 0) * (calculatedItem.amount ?? 0);

    calculatedItem.totalAmount = calculatedItem.price;

    if (item.discountType === 1) {
      calculatedItem.totalAmount -= calculatedItem.discountAmount * calculatedItem.quantity;
    }

    if (item.discountType === 0) {
      calculatedItem.totalAmount -=
        ((calculatedItem.amount * calculatedItem.discountAmount) / 100) * calculatedItem.quantity;
    }

    if (calculatedItem.totalAmount < 0) {
      calculatedItem.totalAmount = 0;
    }

    calculatedItem.finalDiscount = calculatedItem.price - calculatedItem.totalAmount;

    if (item.haveTax || item.tax) {
      calculatedItem.taxtionAmount = (calculatedItem.totalAmount * 10) / 100;
      calculatedItem.totalAmount += calculatedItem.taxtionAmount;
    }

    return calculatedItem;
  };

  const { loading: loadingDetail } = useAsync(async () => {
    if (!code) return false;
    const { attachmentUrls, ...invoice } = await dispatch(
      getInvoiceInfo({ code, returnedForEditCorrection: isCorrectionMode }),
    );

    setSelectedItems(invoice.products?.map(calcItem) as Product[]);
    formStepOne.setFieldsValue({
      invoiceName: !invoice.title ? undefined : invoice.title,
      invoiceNumber: !invoice.number ? undefined : invoice.number,
      isTextReminder: !!invoice.memo,
      isTextUser: !!invoice.notes,
      showNotesAfterSuccessPayment: !!invoice.showNotesAfterSuccessPayment,
      showTermsAfterSuccessPayment: !!invoice.showTermsAfterSuccessPayment,
      customReminderMessage: !invoice.customReminderMessage ? undefined : invoice.customReminderMessage,
      isCustomerMessage: !!invoice.customReminderMessage,
      conditions: !!invoice.termsAndConditions,
      memo: !invoice.memo ? undefined : invoice.memo,
      notes: !invoice.notes ? undefined : invoice.notes,
      termsAndConditions: !invoice.termsAndConditions ? undefined : invoice.termsAndConditions,
    });
    setEnteredText(invoice.termsAndConditions);
    setInvoiceName(invoice.title);
    setInvoiceNumber(invoice.number);
    formStepTwo.setFieldsValue({
      dueDate: !invoice.isSchedule
        ? toUtc({ date: invoice.dueDate, dateFormat: backFormat, toFormat: backFormat }).object || undefined
        : undefined,
      sendDate: !invoice.isSchedule
        ? toUtc({ date: invoice.sendDate, dateFormat: backFormat, toFormat: backFormat }).object || undefined
        : undefined,
    });
    setSentDate(
      toJalali({ date: invoice.sendDate, dateFormat: backFormat, toFormat: " jDD jMMMM jYYYY" }).string || undefined,
    );
    setDueDate(
      toJalali({ date: invoice.dueDate, dateFormat: backFormat, toFormat: " jDD jMMMM jYYYY" }).string || undefined,
    );
    formStepThree.setFieldsValue({
      shipping: invoice.shipping,
      discountAmount: String(invoice.discountAmount),
      discountType: invoice.discountType,
      sendAttachmentsAfterSuccessPayment: !!invoice.sendAttachmentsAfterSuccessPayment,
    });
    setTransportationCost(Boolean(invoice.shipping));
    setShowCoupon(Boolean(invoice.discountAmount));
    setCoupon(invoice.totalDiscountAmount);
    setTransport(invoice.shipping);
    setTotalAmount(invoice.total);
    formStepFour.setFieldsValue({
      customers: invoice.customer
        ? [
            {
              key: invoice.customer.code,
              value: invoice.customer.code,
              label: invoice.customer.fullName,
              email: invoice.customer.email,
              phone: invoice.customer.phone,
            },
          ]
        : [],
    });
    setSelectedCustomer(
      invoice.customer
        ? [
            {
              key: invoice.customer.code,
              fullName: invoice.customer.fullName,
              code: invoice.customer.code,
              email: invoice.customer.email,
              phoneNumber: invoice.customer.phone,
            },
          ]
        : [],
    );
    if (attachmentUrls) {
      setFileList(
        attachmentUrls.map((url: string, index: number) => ({
          name: `${getFileName(url)} ${index + 1}`,
          size: 0,
          type: getFileType(url),
          uid: String(index),
          url,
        })),
      );
    }

    setIsSchedule(invoice.isSchedule ?? false);

    return invoice;
  }, [backFormat, code, dispatch, formStepFour, formStepOne, formStepThree, formStepTwo, isCorrectionMode]);

  const [{ loading: loadingEdit }, handleEdit] = useAsyncFn(
    async (invoiceData: InvoiceEditRequestViewModel) => {
      await dispatch(updateInvoice(invoiceData.code, invoiceData));
    },
    [dispatch],
  );

  const [{ loading: loadingCreate }, handleCreate] = useMessagedAsyncFn(
    async (values: any, type: SubmitType, paidManualDescription?: string) => {
      const createStatus = {
        draft: 0,
        manual: 2,
        send: 1,
      };
      const stepOne = formStepOne.getFieldsValue();
      const stepTwo = formStepTwo.getFieldsValue();
      const stepThree = formStepThree.getFieldsValue();
      const stepFour = formStepFour.getFieldsValue();

      function removePriceUnit(msg: string | undefined): string {
        if (msg) {
          let filteredMessage = ["تومن", "تومان", "ریال"].reduce((prev, current) => {
            const response = prev.replace(RegExp(current, "g"), "");
            response.replace(/(?:)/g, "");
            return response;
          }, msg);
          filteredMessage = filteredMessage.replace(/INVOICE_AMOUNT/g, "INVOICE_AMOUNT تومان ");
          return filteredMessage;
        }
        return msg as string;
      }

      const invoiceData: InvoiceCreateRequestViewModel = {
        ...values,
        attachments: stepThree.attachments
          ? stepThree.attachments.fileList
            ? stepThree.attachments.fileList.map((attach: any) =>
                attach.url ? attach.url.split("/")[attach.url.split("/").length - 1] : attach.response,
              )
            : values.attachments
          : [],
        title: stepOne.invoiceName,
        number: stepOne.invoiceNumber,
        memo: stepOne.memo,
        notes: stepOne.notes,
        tagIds: stepFour.customers?.filter((obj: any) => obj.type === "category").map((tag: any) => tag.key),
        termsAndConditions: stepOne.termsAndConditions,
        showNotesAfterSuccessPayment: stepOne.showNotesAfterSuccessPayment,
        showTermsAfterSuccessPayment: stepOne.showTermsAfterSuccessPayment,
        sendAttachmentsAfterSuccessPayment: stepThree.sendAttachmentsAfterSuccessPayment,
        customReminderMessage: removePriceUnit(stepOne.customReminderMessage),
        discountType: stepThree.discountType,
        createStatus: isCorrectionMode ? 3 : createStatus[type],
        customers: selectedCustomer?.filter((obj: any) => obj.type !== "category").map((c) => ({ code: c.code })) ?? [],
        discountAmount: Number(stepThree.discountAmount ?? 0),
        dueDate: toUtc({ date: stepTwo.dueDate, toFormat: dateFormats.standard.utcDate, convertTime: false }).string,
        paidManualDescription: paidManualDescription || "",
        products: selectedItems,
        sendDate: toUtc({ date: stepTwo.sendDate, toFormat: dateFormats.standard.utcDate, convertTime: false }).string,
        shipping: Number(stepThree.shipping ?? 0),
      };
      if (code) {
        await handleEdit({ code, ...invoiceData });
        if (isCorrectionMode) {
          await message.success({ content: t("invoice.create.successfulCorrection") });
          navigate({ pathname: DashboardRoutes.myShop.invoice.list.url }, { state: location.state });
        } else {
          // Message.success({ content: t("invoice.create.successfulEdit") });
          navigate(
            { pathname: DashboardRoutes.myShop.invoice.show.url },
            {
              state:
                invoiceData.createStatus === InoviceCreateStatus.Draft ||
                invoiceData.createStatus === InoviceCreateStatus.PaidManual
                  ? []
                  : [{ code }],
            },
          );
        }
        return undefined;
      }
      if (isSchedule) {
        const body: InvoiceCreateScheduleViewModel = {
          ...values,
          attachments: stepThree.attachments
            ? stepThree.attachments.fileList
              ? stepThree.attachments.fileList.map((attach: any) =>
                  attach.url ? attach.url.split("/")[attach.url.split("/").length - 1] : attach.response,
                )
              : values.attachments
            : [],
          // cc: values.cc.map((cc: string) => ({ emailAddress: cc })),
          createStatus: isCorrectionMode ? 3 : createStatus[type],
          customers: selectedCustomer?.map((c) => ({ code: c.code })) ?? [],
          discountAmount: Number(stepThree.discountAmount ?? 0),
          discountType: stepThree.discountType,
          paidManualDescription: paidManualDescription || "",
          products: selectedItems,
          title: stepOne.invoiceName,
          number: stepOne.invoiceNumber,
          memo: stepOne.memo,
          notes: stepOne.notes,
          termsAndConditions: stepOne.termsAndConditions,
          showNotesAfterSuccessPayment: stepOne.showNotesAfterSuccessPayment,
          showTermsAfterSuccessPayment: stepOne.showTermsAfterSuccessPayment,
          sendAttachmentsAfterSuccessPayment: stepThree.sendAttachmentsAfterSuccessPayment,
          schedule: {
            count: Number(stepTwo.count ?? 0),
            dueDateAfterHowManyDay: Number(stepTwo.dueDateAfterHowManyDay ?? 0),
            interval: stepTwo.interval,
            scheduleType: stepTwo.scheduleType,
          },
          sendDate: toUtc({ date: stepTwo.sendDate, toFormat: dateFormats.standard.utcDateTime })
            .object?.add("hours", 12)
            .format("YYYY-MM-DDZ"),
          shipping: Number(stepThree.shipping ?? 0),
        };
        const response = await invoiceV2Apis.v2InvoiceSchedulePOST(body);
        if (response) {
          navigate(
            {
              pathname: DashboardRoutes.myShop.invoice.show.url,
            },
            // @ts-ignore : TODO: it should be fixed by Arash Naderi in backend
            { state: response.invoices },
          );
        }
        return response;
      }

      const response = await invoiceV2Apis.v2InvoicePOST(invoiceData);
      if (response) {
        navigate(
          { pathname: DashboardRoutes.myShop.invoice.show.url },
          {
            state:
              invoiceData.createStatus === InoviceCreateStatus.Draft ||
              invoiceData.createStatus === InoviceCreateStatus.PaidManual
                ? []
                : response,
          },
        );
      }
      return response;
    },
    [
      formStepOne,
      formStepTwo,
      formStepThree,
      formStepFour,
      isCorrectionMode,
      selectedCustomer,
      selectedItems,
      code,
      isSchedule,
      handleEdit,
      t,
      navigate,
      location.state,
    ],
    undefined,
    { useProgress: true },
  );
  // indicate kavenegar status undefined as not init flag
  const { value: productGetIntegrationStatus } = useAsync(() => {
    return productApis.productGetIntegrationStatus();
  }, []);

  useEffect(() => {
    // Copy invoice
    if ((location.state as InvoiceComplexDetailViewModel)?.code) {
      const { attachmentUrls, schedule, ...invoice } = location.state as InvoiceComplexDetailViewModel;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedItems(invoice.products?.map(calcItem) as Product[]);

      // copy specific fields [Only for schedule invoice]

      formStepOne.setFieldsValue({
        invoiceName: !invoice.title ? undefined : invoice.title,
        invoiceNumber: !invoice.number ? undefined : invoice.number,
        isTextReminder: !!invoice.memo,
        isTextUser: !!invoice.notes,
        showNotesAfterSuccessPayment: !!invoice.showNotesAfterSuccessPayment,
        showTermsAfterSuccessPayment: !!invoice.showTermsAfterSuccessPayment,
        customReminderMessage: !invoice.customReminderMessage ? undefined : invoice.customReminderMessage,
        isCustomerMessage: !!invoice.customReminderMessage,
        conditions: !!invoice.termsAndConditions,
        memo: !invoice.memo ? undefined : invoice.memo,
        notes: !invoice.notes ? undefined : invoice.notes,
        termsAndConditions: !invoice.termsAndConditions ? undefined : invoice.termsAndConditions,
        ...(isSchedule ? schedule : {}),
      });
      formStepTwo.setFieldsValue({
        dueDate: !invoice.isSchedule
          ? toUtc({ date: invoice.dueDate, dateFormat: backFormat, toFormat: backFormat }).object || undefined
          : undefined,
        sendDate: !invoice.isSchedule
          ? toUtc({ date: invoice.sendDate, dateFormat: backFormat, toFormat: backFormat }).object || undefined
          : undefined,
        ...(isSchedule ? schedule : {}),
      });
      formStepThree.setFieldsValue({
        shipping: invoice.shipping,
        discountAmount: String(invoice.discountAmount),
        discountType: invoice.discountType,
        sendAttachmentsAfterSuccessPayment: !!invoice.sendAttachmentsAfterSuccessPayment,
        ...(isSchedule ? schedule : {}),
      });
      formStepFour.setFieldsValue({
        customers: invoice.customer
          ? [
              {
                key: invoice.customer.code,
                value: invoice.customer.code,
                label: invoice.customer.fullName,
                email: invoice.customer.email,
                phone: invoice.customer.phone,
              },
            ]
          : [],
        ...(isSchedule ? schedule : {}),
      });
      if (attachmentUrls) {
        setFileList(
          attachmentUrls.map((url: string, index: number) => {
            const str = url || "";
            return {
              name: `${getFileName(str)} ${index + 1}`,
              size: 0,
              type: getFileType(str),
              uid: String(index),
              url: str,
            };
          }),
        );
      }

      setIsSchedule(invoice.isSchedule ?? false);
    }
  }, [backFormat, formStepFour, formStepOne, formStepThree, formStepTwo, isSchedule, location.state]);

  // =========================================
  // Subcomponents
  // =========================================
  const loading = loadingCreate || loadingEdit || loadingDetail;
  const resetField = useCallback(() => {
    formStepOne.resetFields();
    formStepTwo.resetFields();
    formStepThree.resetFields();
    formStepFour.resetFields();
  }, [formStepFour, formStepOne, formStepThree, formStepTwo]);

  const clearAnchor = useCallback(() => {
    setInvoiceName("");
    setInvoiceNumber("");
    setSentDate("--");
    setDueDate("--");
    setTotalAmount(0);
    setSumTotalAmount(0);
    setCountProduct(0);
    setCoupon(0);
    setTransport(0);
    setSelectedCustomer([]);
    setShowCoupon(false);
    setTransportationCost(false);
    setSelectedItems([]);
    setFileList([]);
    setCurrents(CreateStepInvoiceEnum.initialSetting);
  }, []);

  useEffect(
    () => () => {
      resetField();
      clearAnchor();
    },
    [clearAnchor, resetField],
  );

  const steps = [
    <FirstStepInvoice
      key={1}
      next={nextStep}
      showInvoiceType={showInvoiceType}
      setShowInvoiceType={setShowInvoiceType}
      loading={loading}
      setInvoiceName={setInvoiceName}
      setInvoiceNumber={setInvoiceNumber}
      form={formStepOne}
      enteredText={enteredText}
      setEnteredText={setEnteredText}
      productGetIntegrationStatus={productGetIntegrationStatus}
    />,
    <SecondStepInvoice
      key={2}
      setSentDate={setSentDate}
      setIsSchedule={setIsSchedule}
      setDueDate={setDueDate}
      prev={prevStep}
      next={nextStep}
      loading={loading}
      form={formStepTwo}
    />,
    <ThirdStepInvoice
      key={3}
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      calcItem={calcItem}
      setCoupon={setCoupon}
      setSumTotalAmount={setSumTotalAmount}
      setCountProduct={setCountProduct}
      fileList={fileList}
      setFileList={setFileList}
      setTransport={setTransport}
      setTotalAmount={setTotalAmount}
      loading={loading}
      prev={prevStep}
      next={nextStep}
      showCoupon={showCoupon}
      setShowCoupon={setShowCoupon}
      transportationCost={transportationCost}
      setTransportationCost={setTransportationCost}
      form={formStepThree}
    />,
    <FourthStepInvoice
      key={4}
      selectedCustomer={selectedCustomer}
      setSelectedCustomer={setSelectedCustomer}
      isCorrectionMode={isCorrectionMode}
      setSubmitType={setSubmitType}
      setManualPaymentNotesModalVisible={setManualPaymentNotesModalVisible}
      manualPaymentNotesModalVisible={manualPaymentNotesModalVisible}
      setInvoiceNotes={setInvoiceNotes}
      prevStep={prevStep}
      loading={loading}
      form={formStepFour}
      handleCreate={handleCreate}
      showInvoiceType={showInvoiceType}
      submitType={submitType}
      invoiceNotes={invoiceNotes}
    />,
  ];

  const getTitle = (title: CreateStepInvoiceEnum) => {
    switch (title) {
      case CreateStepInvoiceEnum.initialSetting:
        return t("shop.invoiceTitle.initialSetting");
      case CreateStepInvoiceEnum.scheduling:
        return t("shop.invoiceTitle.scheduling");
      case CreateStepInvoiceEnum.productConnection:
        return t("shop.invoiceTitle.productConnection");
      case CreateStepInvoiceEnum.customerConnection:
        return t("shop.invoiceTitle.customerConnection");
      default:
        return undefined;
    }
  };

  // =========================================
  // Render
  // =========================================
  return (
    <Page>
      <Drawer
        title={t("invoice.create.createInvoice")}
        open={true}
        onClose={() => {
          resetField();
          clearAnchor();
          navigate({ pathname: DashboardRoutes.myShop.invoice.list.url }, { state: location.state });
        }}
        placement={"bottom"}
        size={"100%"}
        closeIcon={false}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              resetField();
              clearAnchor();
              navigate({ pathname: DashboardRoutes.myShop.invoice.list.url }, { state: location.state });
            }}
          ></Button>
        }
      >
        <CustomFormLayout
          title={
            <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
              <Typography.Title level={4}>{getTitle(currents)}</Typography.Title>
              <Progress steps={4} percent={currents * 25} format={(percent) => `${(percent || 25) / 25} از 4`} />
            </Space>
          }
          firstChild={
            <div>
              <div className={currents !== CreateStepInvoiceEnum.initialSetting ? "tw:hidden" : ""}>{steps[0]}</div>
              <div className={currents !== CreateStepInvoiceEnum.scheduling ? "tw:hidden" : ""}>{steps[1]}</div>
              <div className={currents !== CreateStepInvoiceEnum.productConnection ? "tw:hidden" : ""}>{steps[2]}</div>
              <div className={currents !== CreateStepInvoiceEnum.customerConnection ? "tw:hidden" : ""}>{steps[3]}</div>
            </div>
          }
          secondChild={
            <DataCard
              invoiceName={invoiceName}
              invoiceNumber={invoiceNumber}
              sentDate={sentDate}
              dueDate={dueDate}
              countProduct={countProduct}
              sumTotalAmount={sumTotalAmount}
              coupon={coupon}
              transport={transport}
              totalAmount={totalAmount}
              selectedCustomer={selectedCustomer}
            />
          }
        />
      </Drawer>
    </Page>
  );
};

export default InvoiceForm;
